import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  TextareaAutosize,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Provider } from '@apps/shared/src/providerSearchAdmin/types/providerSearch';
import { setActionInitiated } from '@apps/shared/src/providerSearchAdmin/actions';
import ConfirmationPopup from './ConfirmationPopup';

type UpdatePopupProps = {
  open: boolean;
  actionInitiated: string;
  onClose: () => void;
  onUpdate: (data: UpdateFormData) => void;
  providerData: Provider | null;
  hasError: boolean;
  isActionPending: boolean;
};

type UpdateFormData = {
  providerName: string;
  providerNPI: string;
  providerTaxID: string;
  entityType: string;
  recommendation: string;
  externalNotes: string;
  internalNotes: string;
  active: boolean;
  lastUpdatedDate: Date;
};

type Props = UpdatePopupProps & typeof mapDispatchToProps;

const useStyles = makeStyles(theme => ({
  flexBox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  select: {
    '& .MuiSelect-selectMenu': {
      background: '#ffffff',
    },
    '& .MuiSelect-selectMenu:focus': {
      border: 'none',
    },
  },
  textField: {
    width: '100%',
    marginLeft: theme.spacing(1),
    '& .MuiInputLabel-formControl': {
      paddingLeft: '.5rem',
    },
    '& .MuiInputBase-fullWidth': {
      width: '103% !important',
    },
  },
  header: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '1.4rem',
  },
  label: {
    marginRight: theme.spacing(2),
    minWidth: '10rem',
    display: 'inline-block',
    textAlign: 'right',
    fontWeight: 600,
  },
  textarea: {
    width: '92%',
    padding: '10px 14px',
    borderRadius: 4,
    border: '1px solid #E5EBF5',
    resize: 'none',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  cancelButton: {
    color: '#ffffff',
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  updateButton: {
    color: '#ffffff',
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  customDialog: {
    '& .MuiDialog-container ': {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: '700px !important',
        '& .MuiDialogContent-root': {
          padding: '8px 40px !important',
          overflowY: 'none',
        },
      },
    },
  },
  errorText: {
    color: 'red',
    marginTop: theme.spacing(1),
  },
}));

const UpdateProviderSearch: React.FC<Props> = ({
  open,
  onClose,
  onUpdate,
  providerData,
  setActionInitiated,
  hasError,
  isActionPending,
  actionInitiated,
}: Props) => {
  const classes = useStyles();
  const [formData, setFormData] = useState<UpdateFormData>({
    providerName: '',
    providerNPI: '',
    providerTaxID: '',
    entityType: '',
    recommendation: '',
    externalNotes: '',
    internalNotes: '',
    active: true,
    lastUpdatedDate: new Date(),
  });
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [errors, setErrors] = useState<{ [key in keyof UpdateFormData]?: string }>({});

  useEffect(() => {
    if (providerData) {
      setFormData({
        providerName: providerData.providerName || '',
        providerNPI: providerData.npi || '',
        providerTaxID: providerData.ein || '',
        entityType: providerData.entityType || '',
        recommendation: providerData?.recommendation || '',
        externalNotes: providerData.external_notes || '',
        internalNotes: providerData.internal_notes || '',
        active: providerData.active || true,
        lastUpdatedDate: providerData.updatedDate || new Date(),
      });
    }
  }, [providerData]);

  useEffect(() => {
    if (!isActionPending && !hasError && actionInitiated === 'update') {
      setOpenConfirmation(true);
      onClose();
    }
  }, [isActionPending]);

  useEffect(() => {
    setOpenConfirmation(false);
  }, []);

  const validateForm = () => {
    const newErrors: { [key in keyof UpdateFormData]?: string } = {};
    if (!formData.providerNPI.trim()) {
      newErrors.providerNPI = 'Provider NPI is required';
    } else if (!/^\d{10}$/.test(formData.providerNPI)) {
      newErrors.providerNPI = 'Provider NPI must be a 10-digit number';
    }
    if (!formData.recommendation.trim()) {
      newErrors.recommendation = 'Recommendation is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;

    if (name === 'providerNPI') {
      // Ensure only numbers and max length of 10 for NPI
      const numericValue = value as string;
      if (/^\d*$/.test(numericValue) && numericValue.length <= 10) {
        setFormData({
          ...formData,
          [name]: numericValue,
        });
      }
    } else if (name === 'providerTaxID') {
      // Allow only numbers, max length 9
      const numericValue = value as string;
      if (/^\d*$/.test(numericValue) && numericValue.length <= 9) {
        setFormData({
          ...formData,
          [name]: numericValue,
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [name as string]: value }));
    }
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, active: e.target.checked }));
  };

  const handleUpdate = () => {
    if (validateForm()) {
      onUpdate(formData);
    }
  };

  const handleClose = () => {
    setActionInitiated('');
    setOpenConfirmation(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="update-dialog-title"
        className={classes.customDialog}
      >
        <Typography className={classes.header}>Update Provider Record</Typography>
        <DialogContent>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>
              Provider NPI<span style={{ color: 'red' }}>&nbsp;*</span>
            </Typography>
            <FormControl fullWidth className={classes.textField}>
              <TextField
                required
                name="providerNPI"
                label="Required"
                value={formData.providerNPI}
                onChange={handleFieldChange}
                margin="normal"
                InputProps={{ className: classes.textField }}
                error={!!errors.providerNPI}
              />
              {errors.providerNPI && (
                <Typography variant="caption" className={classes.errorText}>
                  {errors.providerNPI}
                </Typography>
              )}
            </FormControl>
          </div>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>Provider Tax ID</Typography>
            <FormControl fullWidth className={classes.textField}>
              <TextField
                name="providerTaxID"
                label="Optional"
                value={formData.providerTaxID.trim()}
                onChange={handleFieldChange}
                margin="normal"
                InputProps={{ className: classes.textField }}
              />
            </FormControl>
          </div>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>
              Recommendation<span style={{ color: 'red' }}>&nbsp;*</span>
            </Typography>
            <FormControl fullWidth className={classes.textField}>
              <Select
                name="recommendation"
                value={formData.recommendation}
                onChange={handleFieldChange}
                className={classes.select}
                fullWidth
                error={!!errors.recommendation}
              >
                <MenuItem value="CAUTION">Caution</MenuItem>
                <MenuItem value="UNFRIENDLY">Unfriendly</MenuItem>
                <MenuItem value="DELISTED">Delisted</MenuItem>
              </Select>
              {errors.recommendation && (
                <Typography variant="caption" className={classes.errorText}>
                  {errors.recommendation}
                </Typography>
              )}
            </FormControl>
          </div>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>External Notes</Typography>
            <FormControl fullWidth className={classes.textField}>
              <TextareaAutosize
                minRows={4}
                name="externalNotes"
                placeholder="Optional"
                value={formData.externalNotes}
                onChange={handleFieldChange}
                className={classes.textarea}
              />
            </FormControl>
          </div>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>Internal Notes</Typography>
            <FormControl fullWidth className={classes.textField}>
              <TextareaAutosize
                minRows={4}
                name="internalNotes"
                placeholder="Optional"
                value={formData.internalNotes}
                onChange={handleFieldChange}
                className={classes.textarea}
              />
            </FormControl>
          </div>
          <div className={classes.flexBox}>
            <Typography className={classes.label}>Active</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.active}
                  onChange={handleActiveChange}
                  name="active"
                  color="primary"
                />
              }
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} className={classes.cancelButton}>
            Cancel
          </Button>
          <Button onClick={handleUpdate} className={classes.updateButton}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationPopup
        message="Record(s) Successfully Updated"
        open={openConfirmation && !hasError}
        onClose={handleClose}
      />
    </>
  );
};

const mapDispatchToProps = {
  setActionInitiated,
};

export default connect(null, mapDispatchToProps)(UpdateProviderSearch);
